import { FILTER_ROOT_SELECTOR } from "./collectionPage/constants";
import { getNavParentFromLocation } from "./collectionPage/urls";
import { applyCollectionUpdate, fetchCollectionCount } from "./collectionPage/router";

declare global {
  interface Window {
    ProductCardSwatches?: {
      initCard: (card: Element, options?: object) => void;
      initAll: (root?: ParentNode, options?: object) => void;
    };
  }
}

let globalAbort: AbortController | null = null;
let isInitialFetch = true;

const filterRoot = (scope: ParentNode = document) => scope.querySelector<HTMLElement>(FILTER_ROOT_SELECTOR);

const getFormParams = (form: HTMLFormElement) => {
  const formData = new FormData(form);
  const params = new URLSearchParams();
  formData.forEach((value, key) => {
    if (typeof value === "string") params.append(key, value);
  });
  return params.toString();
};

const getResultText = (root: HTMLElement | null, count: string) => {
  if (!root) {
    if (count === "0") return "Keine Ergebnisse";
    if (count === "1") return "1 Ergebnis anzeigen";
    return `${count} Ergebnisse anzeigen`;
  }

  if (count === "0") return root.dataset.filterResultsZero ?? "Keine Ergebnisse";
  if (count === "1") return root.dataset.filterResultsOne ?? "1 Ergebnis anzeigen";
  const template = root.dataset.filterResultsOther ?? "{{ count }} Ergebnisse anzeigen";
  return template.replace("{{ count }}", count);
};

const updateButtonState = (button: HTMLInputElement, count: string) => {
  const disabled = count === "0";
  button.disabled = disabled;
  button.style.opacity = disabled ? "0.4" : "1";
};

const ensureBodyScrollRestored = (root: HTMLElement) => {
  const filterModal = root.querySelector(".filter-modal");
  if (!filterModal || filterModal.classList.contains("opacity-0") || filterModal.classList.contains("pointer-events-none")) {
    document.body.style.overflow = "";
  }
};

const toggleFilterModal = (root: HTMLElement) => {
  const filterModal = root.querySelector<HTMLElement>(".filter-modal");
  if (!filterModal) return;
  const filterOverlay = filterModal.querySelector<HTMLElement>(".filter-modal-overlay");
  const filterContent = filterModal.querySelector<HTMLElement>(".bg-white");
  const isHidden = filterModal.classList.contains("opacity-0");

  if (isHidden) {
    filterModal.classList.remove("pointer-events-none", "opacity-0");
    filterOverlay?.classList.remove("opacity-0");
    filterContent?.classList.remove("translate-y-full");
    filterModal.setAttribute("aria-modal", "true");
    document.body.style.overflow = "hidden";
  } else {
    filterModal.classList.add("opacity-0");
    filterOverlay?.classList.add("opacity-0");
    filterContent?.classList.add("translate-y-full");
    filterModal.removeAttribute("aria-modal");
    document.body.style.overflow = "";
    setTimeout(() => {
      filterModal.classList.add("pointer-events-none");
    }, 300);
  }
};

const toggleMobileFilter = (root: HTMLElement, button: HTMLElement) => {
  const filter = button.closest<HTMLElement>(".mobile-filter");
  if (!filter) return;
  const content = filter.querySelector<HTMLElement>(".mobile-filter-content");
  const plusIcon = button.querySelector<HTMLElement>(".plus-icon");
  const minusIcon = button.querySelector<HTMLElement>(".minus-icon");
  if (!content || !plusIcon || !minusIcon) return;

  for (const other of Array.from(root.querySelectorAll<HTMLElement>(".mobile-filter"))) {
    if (other === filter) continue;
    const otherContent = other.querySelector<HTMLElement>(".mobile-filter-content");
    const otherPlus = other.querySelector<HTMLElement>(".plus-icon");
    const otherMinus = other.querySelector<HTMLElement>(".minus-icon");
    if (!otherContent || !otherPlus || !otherMinus) continue;
    otherContent.style.maxHeight = "0";
    otherContent.style.paddingTop = "0";
    otherContent.style.paddingBottom = "0";
    otherPlus.classList.remove("hidden");
    otherMinus.classList.add("hidden");
    other.querySelector(".mobile-filter-button")?.setAttribute("aria-expanded", "false");
  }

  const isClosed = !content.style.maxHeight || content.style.maxHeight === "0px";
  if (isClosed) {
    content.style.maxHeight = `${content.scrollHeight + 25}px`;
    content.style.paddingTop = "10px";
    content.style.paddingBottom = "15px";
    plusIcon.classList.add("hidden");
    minusIcon.classList.remove("hidden");
    button.setAttribute("aria-expanded", "true");
  } else {
    content.style.maxHeight = "0";
    content.style.paddingTop = "0";
    content.style.paddingBottom = "0";
    plusIcon.classList.remove("hidden");
    minusIcon.classList.add("hidden");
    button.setAttribute("aria-expanded", "false");
  }
};

const toggleFilter = (root: HTMLElement, button: HTMLElement) => {
  const popups = root.querySelectorAll<HTMLElement>(".filter-popup");
  const popup = button.nextElementSibling as HTMLElement | null;
  if (!popup) return;

  for (const other of Array.from(popups)) {
    if (other === popup || other.classList.contains("hidden")) continue;
    other.classList.add("opacity-0", "translate-y-2");
    const otherBtn = other.previousElementSibling as HTMLElement | null;
    otherBtn?.querySelector(".label")?.classList.remove("opacity-0");
    otherBtn?.querySelector(".label-active")?.classList.add("opacity-0");
    otherBtn?.setAttribute("aria-expanded", "false");
    setTimeout(() => other.classList.add("hidden"), 200);
  }

  const label = button.querySelector<HTMLElement>(".label");
  const labelActive = button.querySelector<HTMLElement>(".label-active");

  if (popup.classList.contains("hidden")) {
    popup.classList.remove("hidden");
    popup.offsetHeight;
    popup.classList.remove("opacity-0", "translate-y-2");
    label?.classList.add("opacity-0");
    labelActive?.classList.remove("opacity-0");
    button.setAttribute("aria-expanded", "true");
  } else {
    popup.classList.add("opacity-0", "translate-y-2");
    label?.classList.remove("opacity-0");
    labelActive?.classList.add("opacity-0");
    button.setAttribute("aria-expanded", "false");
    setTimeout(() => popup.classList.add("hidden"), 200);
  }
};

const updateMobileActiveFilters = (root: HTMLElement) => {
  const form = root.querySelector<HTMLFormElement>(".filter-form-mobile");
  const activeContainer = form?.previousElementSibling as HTMLElement | null;
  if (!form || !activeContainer) return;
  const activeList = activeContainer.querySelector<HTMLElement>(".flex.gap-3");
  if (!activeList) return;

  const checked = form.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked');
  const template = root.querySelector<HTMLTemplateElement>(".filter-tag-template");
  if (!template) return;

  if (checked.length) {
    activeContainer.classList.remove("hidden");
    activeList.innerHTML = "";
    for (const input of Array.from(checked)) {
      const label = input.closest("label");
      if (!label) continue;
      const filterTag = (template.content.cloneNode(true) as DocumentFragment).querySelector("a") as HTMLAnchorElement | null;
      if (!filterTag) continue;
      const labelEl = filterTag.querySelector(".filter-label");
      if (labelEl) labelEl.textContent = label.querySelector(".filter-label")?.textContent || "";

      const swatchDiv = label.querySelector<HTMLElement>(".peer-checked\\:hidden");
      const isSwatch = swatchDiv?.closest("label")?.querySelector('[style*="background"]');
      if (isSwatch && swatchDiv) {
        const colorSwatch = filterTag.querySelector<HTMLElement>(".color-swatch");
        const imageSwatch = filterTag.querySelector<HTMLElement>(".image-swatch");
        const bgImage = swatchDiv.style.backgroundImage;
        if (bgImage && imageSwatch) {
          imageSwatch.classList.remove("hidden");
          imageSwatch.style.backgroundImage = bgImage;
        } else if (colorSwatch) {
          colorSwatch.classList.remove("hidden");
          colorSwatch.style.backgroundColor = swatchDiv.style.backgroundColor;
        }
      }

      filterTag.addEventListener("click", async (e) => {
        e.preventDefault();
        input.checked = false;
        input.dispatchEvent(new Event("change"));
        const params = getFormParams(form);
        const count = await fetchFilterCount(params);
        const mobileBtn = form.querySelector<HTMLInputElement>('input[type="submit"]');
        if (count && mobileBtn) {
          mobileBtn.value = getResultText(root, count);
          updateButtonState(mobileBtn, count);
        }
      });

      activeList.appendChild(filterTag);
    }
  } else {
    activeContainer.classList.add("hidden");
  }
};

const applyFilterUpdate = async (pathname: string, params: string, countOnly: boolean) => {
  const navParent = getNavParentFromLocation();
  const search = params ? `?${params}` : "";

  if (countOnly) {
    return fetchCollectionCount(pathname, search, navParent);
  }

  if (isInitialFetch) {
    isInitialFetch = false;
    return fetchCollectionCount(pathname, search, navParent);
  }

  const root = filterRoot();
  try {
    await applyCollectionUpdate({
      pathname,
      search,
      navParent,
      mode: "filter",
      pushHistory: true,
    });
    ensureBodyScrollRestored(root ?? document.body);
    return fetchCollectionCount(pathname, search, navParent);
  } catch {
    const gridContainer = document.querySelector<HTMLElement>("#products-grid");
    if (gridContainer) {
      gridContainer.style.opacity = "1";
      gridContainer.removeAttribute("aria-busy");
    }
    if (root) ensureBodyScrollRestored(root);
    return null;
  }
};

const fetchFilterCount = async (params: string, pathnameOverride?: string, countOnly = false) => {
  const basePath = pathnameOverride || window.location.pathname;
  return applyFilterUpdate(basePath, params, countOnly);
};

const bindActiveFilterRemovalLinks = (root: HTMLElement) => {
  for (const link of Array.from(root.querySelectorAll<HTMLAnchorElement>('a[href*="/collections/"]'))) {
    if (link.closest(".filter-form") || link.closest(".filter-form-mobile") || link.closest(".filter-popup")) continue;
    if (link.dataset.ekFilterBound === "true") continue;
    link.dataset.ekFilterBound = "true";
    link.addEventListener("click", async (e) => {
      e.preventDefault();
      const url = new URL(link.href, window.location.origin);
      await fetchFilterCount(url.searchParams.toString(), url.pathname);
    });
  }
};

const setupFilterRoot = (root: HTMLElement, signal: AbortSignal) => {
  for (const btn of Array.from(root.querySelectorAll<HTMLElement>(".filter-button"))) {
    btn.setAttribute("aria-expanded", "false");
    btn.addEventListener("click", () => toggleFilter(root, btn), { signal });
  }

  for (const btn of Array.from(root.querySelectorAll<HTMLElement>(".mobile-filter-button"))) {
    btn.setAttribute("aria-expanded", "false");
    btn.addEventListener("click", () => toggleMobileFilter(root, btn), { signal });
  }

  const mobileToggle = root.querySelector<HTMLElement>(".mobile-filter-toggle");
  mobileToggle?.addEventListener("click", () => toggleFilterModal(root), { signal });

  for (const modalEl of Array.from(root.querySelectorAll<HTMLElement>(".modal-close, .filter-modal-overlay"))) {
    modalEl.addEventListener("click", () => toggleFilterModal(root), { signal });
  }

  const filterModalElement = root.querySelector<HTMLElement>(".filter-modal");
  filterModalElement?.addEventListener(
    "click",
    (event) => {
      if (event.target === filterModalElement) toggleFilterModal(root);
    },
    { signal },
  );

  document.addEventListener(
    "keydown",
    (event) => {
      if (event.key !== "Escape") return;
      const filterModal = root.querySelector<HTMLElement>(".filter-modal");
      if (filterModal && !filterModal.classList.contains("opacity-0") && !filterModal.classList.contains("pointer-events-none")) {
        toggleFilterModal(root);
        event.preventDefault();
        return;
      }
      const openPopups = root.querySelectorAll<HTMLElement>(".filter-popup:not(.hidden)");
      if (openPopups.length === 0) return;
      for (const popup of Array.from(openPopups)) {
        const button = popup.previousElementSibling as HTMLElement | null;
        if (button) toggleFilter(root, button);
      }
      event.preventDefault();
    },
    { signal },
  );

  document.addEventListener(
    "click",
    (event) => {
      if (!window.matchMedia("(min-width: 768px)").matches) return;
      const openPopups = root.querySelectorAll<HTMLElement>(".filter-popup:not(.hidden)");
      if (openPopups.length === 0) return;
      for (const popup of Array.from(openPopups)) {
        const button = popup.previousElementSibling as HTMLElement | null;
        if (!button) continue;
        const target = event.target as Node;
        if (!popup.contains(target) && !button.contains(target)) toggleFilter(root, button);
      }
    },
    { signal },
  );

  for (const sub of Array.from(root.querySelectorAll<HTMLInputElement>(".filter-submit"))) {
    sub.addEventListener(
      "click",
      (e) => {
        e.preventDefault();
        toggleFilterModal(root);
      },
      { signal },
    );
  }

  const desktopForm = root.querySelector<HTMLFormElement>(".filter-form");
  if (desktopForm) {
    const desktopButtons = desktopForm.querySelectorAll<HTMLInputElement>('input[type="submit"]');
    desktopForm.addEventListener(
      "change",
      async () => {
        const params = getFormParams(desktopForm);
        const count = await fetchFilterCount(params);
        if (!count) return;
        for (const btn of Array.from(desktopButtons)) {
          btn.value = getResultText(root, count);
          updateButtonState(btn, count);
        }
      },
      { signal },
    );
  }

  const mobileForm = root.querySelector<HTMLFormElement>(".filter-form-mobile");
  if (mobileForm) {
    mobileForm.addEventListener(
      "submit",
      async (e) => {
        e.preventDefault();
        const params = getFormParams(mobileForm);
        const count = await fetchFilterCount(params);
        const mobileBtn = mobileForm.querySelector<HTMLInputElement>('input[type="submit"]');
        if (count && mobileBtn) {
          mobileBtn.value = getResultText(root, count);
          updateButtonState(mobileBtn, count);
        }
        toggleFilterModal(root);
      },
      { signal },
    );

    for (const cb of Array.from(mobileForm.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'))) {
      cb.addEventListener(
        "change",
        (e) => {
          e.preventDefault();
          updateMobileActiveFilters(root);
        },
        { signal },
      );
    }

    mobileForm.addEventListener(
      "change",
      async () => {
        const params = getFormParams(mobileForm);
        const count = await fetchFilterCount(params);
        const mobileBtn = mobileForm.querySelector<HTMLInputElement>('input[type="submit"]');
        if (count && mobileBtn) {
          mobileBtn.value = getResultText(root, count);
          updateButtonState(mobileBtn, count);
        }
      },
      { signal },
    );
  }

  updateMobileActiveFilters(root);
  bindActiveFilterRemovalLinks(root);
};

const updateSubmitButtons = (count: string) => {
  const root = filterRoot();
  for (const btn of Array.from(document.querySelectorAll<HTMLInputElement>('input[type="submit"]'))) {
    btn.value = getResultText(root, count);
    updateButtonState(btn, count);
  }
};

export const initResultCount = async () => {
  // Runs on every page via scripts.ts — only collection pages have filters to label
  if (!filterRoot()) return;

  // The count section is part of the collection template, so the initial value is already rendered
  const renderedCount = document.querySelector("#collection-count")?.textContent?.trim();
  if (renderedCount) {
    updateSubmitButtons(renderedCount);
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete("nav_parent");
  const initialCount = await fetchFilterCount(searchParams.toString(), undefined, true);
  if (!initialCount) return;
  updateSubmitButtons(initialCount);
};

export const refreshResultCounts = async () => {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete("nav_parent");
  const count = await fetchFilterCount(searchParams.toString(), undefined, true);
  if (!count) return;
  updateSubmitButtons(count);
};

export const initCollectionFilters = (scope: ParentNode = document) => {
  const root = filterRoot(scope);
  if (!root) return;

  globalAbort?.abort();
  globalAbort = new AbortController();
  const { signal } = globalAbort;

  setupFilterRoot(root, signal);
};

export { fetchFilterCount };
